import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Descriptions, Button, Space, Tag, Spin, Alert, Modal, message } from 'antd';
import { EditOutlined, DeleteOutlined, ArrowLeftOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { getContact, deleteContact, type Contact } from '@/api/contactApi';
import { getCustomer, type Customer } from '@/api/customerApi';
import { useAuth } from '@/hooks/useAuth';

export default function ContactDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const [contact, setContact] = useState<Contact | null>(null);
  const [customerName, setCustomerName] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const canWrite = hasPermission('contacts', 'write');
  const canDelete = hasPermission('contacts', 'delete');

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    getContact(id)
      .then((c) => {
        setContact(c);
        if (c.customer_id) {
          getCustomer(c.customer_id)
            .then((cust) => setCustomerName(cust.name))
            .catch(() => setCustomerName(c.customer_id));
        }
      })
      .catch((err) => setError(err instanceof Error ? err.message : t('app.not_found')))
      .finally(() => setLoading(false));
  }, [id, t]);

  const handleDelete = () => {
    if (!id) return;
    Modal.confirm({
      title: t('app.confirm'),
      content: t('contacts.confirm_delete'),
      okText: t('app.yes'),
      cancelText: t('app.no'),
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteContact(id);
          message.success(t('contacts.delete_success'));
          navigate('/contacts');
        } catch {
          message.error(t('app.error'));
        }
      },
    });
  };

  if (loading) {
    return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  }

  if (error || !contact) {
    return (
      <div>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/contacts')}>
          {t('app.back')}
        </Button>
        <Alert type="error" message={error || t('app.not_found')} style={{ marginTop: 16 }} />
      </div>
    );
  }

  const formatDate = (val: string) => (val ? new Date(val).toLocaleString() : '-');

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/contacts')}>
          {t('app.back')}
        </Button>
        {canWrite && (
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => navigate(`/contacts/${id}/edit`)}
          >
            {t('app.edit')}
          </Button>
        )}
        {canDelete && (
          <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
            {t('app.delete')}
          </Button>
        )}
      </Space>

      <h2>{t('contacts.detail_title')}</h2>

      <Descriptions bordered column={{ xs: 1, sm: 2 }}>
        <Descriptions.Item label={t('contacts.field.name')}>{contact.name}</Descriptions.Item>
        <Descriptions.Item label={t('contacts.field.is_primary')}>
          {contact.is_primary ? (
            <Tag color="green" icon={<CheckCircleOutlined />}>
              {t('contacts.primary_yes')}
            </Tag>
          ) : (
            t('contacts.primary_no')
          )}
        </Descriptions.Item>
        <Descriptions.Item label={t('contacts.field.email')}>{contact.email || '-'}</Descriptions.Item>
        <Descriptions.Item label={t('contacts.field.phone')}>{contact.phone || '-'}</Descriptions.Item>
        <Descriptions.Item label={t('contacts.field.title')}>{contact.title || '-'}</Descriptions.Item>
        <Descriptions.Item label={t('contacts.field.department')}>{contact.department || '-'}</Descriptions.Item>
        <Descriptions.Item label={t('contacts.field.customer_id')}>
          <Link to={`/customers/${contact.customer_id}`}>
            {customerName || contact.customer_id}
          </Link>
        </Descriptions.Item>
        <Descriptions.Item label={t('contacts.field.branch_id')}>{contact.branch_id || '-'}</Descriptions.Item>
        <Descriptions.Item label={t('contacts.field.tags')}>
          {(contact.tags?.length ?? 0) > 0
            ? contact.tags.map((tag) => <Tag key={tag}>{tag}</Tag>)
            : '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('contacts.field.notes')} span={2}>
          {contact.notes || '-'}
        </Descriptions.Item>
        <Descriptions.Item label={t('contacts.field.created_by')}>{contact.created_by || '-'}</Descriptions.Item>
        <Descriptions.Item label={t('contacts.field.created_at')}>{formatDate(contact.created_at)}</Descriptions.Item>
        <Descriptions.Item label={t('contacts.field.updated_by')}>{contact.updated_by || '-'}</Descriptions.Item>
        <Descriptions.Item label={t('contacts.field.updated_at')}>{formatDate(contact.updated_at)}</Descriptions.Item>
      </Descriptions>
    </div>
  );
}
